export type ExternalLinkLanguage = 'en' | 'fr' | 'de' | 'it'

const SUPPORTED_LANGUAGES: ExternalLinkLanguage[] = ['en', 'fr', 'de', 'it']
const LANGUAGE_PATH_SEGMENT = /\/(?:en|fr|de|it)(?=\/|$)/
const LANGUAGE_QUERY_PARAMETERS = ['lang', 'language', 'locale'] as const

export const normalizeExternalLinkLanguage = (
  language?: string
): ExternalLinkLanguage => {
  const normalizedLanguage = language?.toLowerCase().split(/[-_]/)[0]

  return SUPPORTED_LANGUAGES.includes(
    normalizedLanguage as ExternalLinkLanguage
  )
    ? (normalizedLanguage as ExternalLinkLanguage)
    : 'en'
}

export const getRegblUrl = (
  egid: number | string,
  language?: string
): string => {
  const normalizedLanguage = normalizeExternalLinkLanguage(language)
  const regblLanguage = normalizedLanguage === 'en' ? 'fr' : normalizedLanguage

  return `https://www.housing-stat.ch/${regblLanguage}/data/query/egid.html?egid=${egid}`
}

const updateLanguageQueryParameter = (
  searchParams: URLSearchParams,
  language: ExternalLinkLanguage
): boolean => {
  let updated = false

  LANGUAGE_QUERY_PARAMETERS.forEach((parameter) => {
    if (searchParams.has(parameter)) {
      searchParams.set(parameter, language)
      updated = true
    }
  })

  return updated
}

const localizeHashRoute = (
  hash: string,
  language: ExternalLinkLanguage
): { hash: string; updated: boolean } => {
  const queryStart = hash.indexOf('?')

  if (queryStart === -1) {
    const localizedHash = hash.replace(LANGUAGE_PATH_SEGMENT, `/${language}`)

    return { hash: localizedHash, updated: localizedHash !== hash }
  }

  const route = hash.slice(0, queryStart)
  const query = hash.slice(queryStart + 1)
  const localizedRoute = route.replace(LANGUAGE_PATH_SEGMENT, `/${language}`)
  const searchParams = new URLSearchParams(query)
  const updatedQuery = updateLanguageQueryParameter(searchParams, language)
  const updatedRoute = localizedRoute !== route

  return {
    hash: updatedQuery
      ? `${localizedRoute}?${searchParams.toString()}`
      : updatedRoute
        ? `${localizedRoute}?${query}`
        : hash,
    updated: updatedQuery || updatedRoute,
  }
}

export const localizeGeoPortalUrl = (
  url: string,
  language?: string
): string => {
  const requestedLanguage = normalizeExternalLinkLanguage(language)

  try {
    const parsedUrl = new URL(url)
    const supportsEnglish =
      parsedUrl.hostname === 'geo.admin.ch' ||
      parsedUrl.hostname.endsWith('.geo.admin.ch') ||
      /\/en(?:\/|$)/.test(parsedUrl.pathname) ||
      /(?:[?&#](?:lang|language|locale)=)en(?:[-_][^&#]+)?(?:[&#]|$)/i.test(
        parsedUrl.toString()
      )
    const normalizedLanguage =
      requestedLanguage === 'en' && !supportsEnglish ? 'fr' : requestedLanguage

    parsedUrl.pathname = parsedUrl.pathname.replace(
      LANGUAGE_PATH_SEGMENT,
      `/${normalizedLanguage}`
    )

    const updatedSearch = updateLanguageQueryParameter(
      parsedUrl.searchParams,
      normalizedLanguage
    )
    const localizedHash = localizeHashRoute(parsedUrl.hash, normalizedLanguage)

    parsedUrl.hash = localizedHash.hash

    if (
      parsedUrl.hostname === 'map.geo.admin.ch' &&
      !updatedSearch &&
      !localizedHash.updated
    ) {
      parsedUrl.searchParams.set('lang', normalizedLanguage)
    }

    return parsedUrl.toString()
  } catch {
    return url
  }
}
